import type { ClientGameContext, ClientGameDefinition } from '@pixelworld/game-core/client';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { da } from '../i18n/da';
import styles from './GameHost.module.css';

interface GameHostProps {
  game: ClientGameDefinition;
  context: ClientGameContext;
  onLoaded?: () => void;
}

/** Loads a game's client code and mounts it into a full-size element. React never touches what the game draws. */
export function GameHost({ game, context, onLoaded }: GameHostProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const notifyLoaded = useEffectEvent(() => onLoaded?.());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let destroy: (() => void) | undefined;
    game.load().then(
      (module) => {
        if (cancelled) return;
        const handle = module.mount(container, context);
        destroy = () => handle.destroy();
        notifyLoaded();
      },
      (loadError: unknown) => {
        console.error('Could not load game', loadError);
        if (!cancelled) setError(da.play.gameLoadFailed);
      },
    );

    return () => {
      cancelled = true;
      destroy?.();
    };
  }, [game, context]);

  return (
    <div className={styles.host} ref={containerRef}>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
